import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  User,
  Lock,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Building2,
  Mail,
  Eye,
  EyeOff,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/authService';
import keycloak from '@/keycloak';
import { ROUTES } from '@/config/routes';
import {
  acceptInviteSchema,
  type AcceptInviteFormData,
} from '@/utils/validators';

type TerminalState =
  | { kind: 'used' }
  | { kind: 'expired' }
  | { kind: 'not_found' };

function extractErrorMessage(err: unknown): string {
  const response = (err as { response?: { data?: { message?: unknown } } })?.response;
  const raw = response?.data?.message;
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (err instanceof Error && err.message) return err.message;
  return 'Failed to accept invitation';
}

function classifyTerminalError(err: unknown): TerminalState | null {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const message = extractErrorMessage(err).toLowerCase();
  if (status === 400 && message.includes('already been used')) return { kind: 'used' };
  if (status === 400 && message.includes('expired')) return { kind: 'expired' };
  if (status === 404 && (message.includes('invitation not found') || message.includes('invalid'))) {
    return { kind: 'not_found' };
  }
  return null;
}

export const AcceptInviteForm = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { acceptInvite, isLoading, error: authError, login } = useAuthStore();
  const [terminal, setTerminal] = useState<TerminalState | null>(null);
  const [checkingToken, setCheckingToken] = useState<boolean>(Boolean(token));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteData, setInviteData] = useState<{
    email?: string;
    role?: string;
    organizationName?: string;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setCheckingToken(false);
      return;
    }
    let cancelled = false;
    setCheckingToken(true);
    authService
      .getInviteStatus(token)
      .then((res) => {
        if (cancelled) return;
        if (res.status === 'used') setTerminal({ kind: 'used' });
        else if (res.status === 'expired') setTerminal({ kind: 'expired' });
        else if (res.status === 'not_found') setTerminal({ kind: 'not_found' });
        else if (res.status === 'valid') {
          setInviteData({
            email: res.email,
            role: res.role,
            organizationName: res.organizationName,
          });
        }
      })
      .catch(() => {
        // Probe failure: let the user attempt the form so they can still get a real error.
      })
      .finally(() => {
        if (!cancelled) setCheckingToken(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const form = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: {
      token,
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const watchPassword = form.watch('password') || '';
  const watchConfirmPassword = form.watch('confirmPassword') || '';

  const passwordRules = [
    { label: 'At least 8 characters', met: watchPassword.length >= 8 },
    { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(watchPassword) },
    { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(watchPassword) },
    { label: 'One number (0-9)', met: /[0-9]/.test(watchPassword) },
    { label: 'One special symbol (!@#$...)', met: /[^a-zA-Z0-9]/.test(watchPassword) },
  ];

  const rulesPassed = passwordRules.filter((r) => r.met).length;
  const passwordsMatch = watchPassword.length > 0 && watchPassword === watchConfirmPassword;

  const strengthText =
    rulesPassed <= 2 ? 'Weak' : rulesPassed <= 4 ? 'Moderate' : 'Strong';

  const onSubmit = async (data: AcceptInviteFormData) => {
    try {
      await acceptInvite({
        token: data.token,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        password: data.password,
      });

      // Automatically authenticate via Keycloak direct access grant so user doesn't have to log in a second time
      const emailToLogin = inviteData?.email;
      if (emailToLogin) {
        try {
          const kcUrl = (keycloak as any)?.authServerUrl || (keycloak as any)?.url || '';
          const realm = keycloak.realm || 'EthioCert';
          const clientId = keycloak.clientId || 'platform-web';
          if (kcUrl) {
            const tokenRes = await axios.post(
              `${kcUrl.replace(/\/+$/, '')}/realms/${realm}/protocol/openid-connect/token`,
              new URLSearchParams({
                client_id: clientId,
                grant_type: 'password',
                username: emailToLogin,
                password: data.password,
              }),
              {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              },
            );

            if (tokenRes.data?.access_token) {
              const tokens = tokenRes.data;
              try {
                sessionStorage.setItem(
                  'kibo_auth_tokens',
                  JSON.stringify({
                    token: tokens.access_token,
                    refreshToken: tokens.refresh_token,
                    idToken: tokens.id_token,
                  }),
                );
              } catch {}

              keycloak.token = tokens.access_token;
              keycloak.refreshToken = tokens.refresh_token;
              keycloak.idToken = tokens.id_token;
              keycloak.authenticated = true;

              try {
                const base64Url = tokens.access_token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                keycloak.tokenParsed = JSON.parse(window.atob(base64));
              } catch {}

              await useAuthStore.getState().initialize();
              toast.success(`Welcome to ${inviteData?.organizationName || 'BlueB'}!`);
              window.location.assign(ROUTES.HOME);
              return;
            }
          }
        } catch (authDirectErr) {
          console.warn('Direct sign-in attempt failed, falling back to login:', authDirectErr);
        }
      }

      toast.success('Account activated! Please sign in with your new password.');
      login();
    } catch (err) {
      const terminalState = classifyTerminalError(err);
      if (terminalState) {
        setTerminal(terminalState);
        return;
      }
      toast.error(extractErrorMessage(err));
    }
  };

  const inputClassName =
    'pl-10 h-11 text-[13px] bg-muted/50  border-border  focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary/50';

  // No token in URL
  if (!token) {
    return (
      <div className="bg-white dark:bg-card rounded-xl border border-border  shadow-sm p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 dark:bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground dark:text-foreground tracking-tight">
              Invalid Invitation Link
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              This invitation link is missing a token. Please check the link in
              your email and try again.
            </p>
          </div>
          <button
            type="button"
            onClick={() => login()}
            className="text-[13px] font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (checkingToken) {
    return (
      <div className="bg-white dark:bg-card rounded-xl border border-border  shadow-sm p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground">
            Checking your invitation…
          </p>
        </div>
      </div>
    );
  }

  if (terminal) {
    const copy = {
      used: {
        Icon: CheckCircle2,
        iconWrap: 'bg-emerald-50 dark:bg-emerald-500/10',
        iconColor: 'text-emerald-500',
        title: 'Invitation already accepted',
        body:
          'This invitation link has already been used. Your account is set up — please sign in with the password you created.',
      },
      expired: {
        Icon: AlertCircle,
        iconWrap: 'bg-amber-50 dark:bg-amber-500/10',
        iconColor: 'text-amber-500',
        title: 'Invitation expired',
        body:
          'This invitation link has expired. Please ask your administrator to send a new one.',
      },
      not_found: {
        Icon: AlertCircle,
        iconWrap: 'bg-red-50 dark:bg-red-500/10',
        iconColor: 'text-red-500',
        title: 'Invalid invitation',
        body:
          'We could not find this invitation. The link may be malformed or no longer valid.',
      },
    }[terminal.kind];

    const { Icon } = copy;

    return (
      <div className="bg-white dark:bg-card rounded-xl border border-border  shadow-sm p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className={`flex items-center justify-center h-12 w-12 rounded-full ${copy.iconWrap}`}>
            <Icon className={`h-6 w-6 ${copy.iconColor}`} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground dark:text-foreground tracking-tight">
              {copy.title}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {copy.body}
            </p>
          </div>
          <button
            type="button"
            onClick={() => login()}
            className="text-[13px] font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-card rounded-xl border border-border shadow-sm p-8 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
          <Building2 className="h-3.5 w-3.5" />
          <span>{inviteData?.organizationName || 'Organization Invitation'}</span>
        </div>
        <h2 className="text-xl font-semibold text-foreground tracking-tight">
          Accept Invitation
        </h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Set up your account to join <strong className="text-foreground">{inviteData?.organizationName || 'the organization'}</strong>
          {inviteData?.role && (
            <span> as <span className="capitalize font-medium text-foreground">{inviteData.role}</span></span>
          )}
        </p>
      </div>

      {/* Read-only Invited Email Banner */}
      {inviteData?.email && (
        <div className="mb-5 flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3.5 py-2.5 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4 text-primary" />
            <span>Invited Account:</span>
          </div>
          <span className="font-semibold text-foreground">{inviteData.email}</span>
        </div>
      )}

      {/* Error Alert */}
      {authError && (
        <div className="mb-6 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-[13px] text-red-700 dark:text-red-400">
          {authError}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Hidden token field */}
          <input type="hidden" {...form.register('token')} />

          {/* Account Section */}
          <div className="space-y-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Account Details
            </p>

            {/* First & Last name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[13px] font-medium text-foreground dark:text-muted-foreground">
                      First name
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Jane"
                          autoComplete="given-name"
                          disabled={isLoading}
                          className={inputClassName}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[13px] font-medium text-foreground dark:text-muted-foreground">
                      Last name
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Doe"
                          autoComplete="family-name"
                          disabled={isLoading}
                          className={inputClassName}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[13px] font-medium text-foreground dark:text-muted-foreground">
                    Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="••••••••"
                        type={showPassword ? 'text' : 'password'}
                        disabled={isLoading}
                        className={`${inputClassName} pr-10`}
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Password strength & rules checklist */}
            {watchPassword.length > 0 && (
              <div className="rounded-lg border border-border/80 bg-muted/40 p-3 space-y-2.5 text-xs">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground font-medium">Password strength:</span>
                  <span
                    className={`font-semibold ${
                      rulesPassed <= 2
                        ? 'text-red-500'
                        : rulesPassed <= 4
                        ? 'text-amber-500'
                        : 'text-emerald-500'
                    }`}
                  >
                    {strengthText}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden flex gap-1">
                  <div
                    className={`h-full transition-all duration-300 rounded-full ${
                      rulesPassed === 0
                        ? 'w-0'
                        : rulesPassed === 1
                        ? 'w-1/5 bg-red-500'
                        : rulesPassed === 2
                        ? 'w-2/5 bg-red-500'
                        : rulesPassed === 3
                        ? 'w-3/5 bg-amber-500'
                        : rulesPassed === 4
                        ? 'w-4/5 bg-amber-500'
                        : 'w-full bg-emerald-500'
                    }`}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                  {passwordRules.map((rule) => (
                    <div
                      key={rule.label}
                      className={`flex items-center gap-1.5 text-[11px] ${
                        rule.met
                          ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {rule.met ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      ) : (
                        <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 mx-1 shrink-0" />
                      )}
                      <span>{rule.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[13px] font-medium text-foreground dark:text-muted-foreground">
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="••••••••"
                        type={showConfirmPassword ? 'text' : 'password'}
                        disabled={isLoading}
                        className={`${inputClassName} pr-10`}
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  {watchConfirmPassword.length > 0 && (
                    <p
                      className={`text-[11px] mt-1 flex items-center gap-1 ${
                        passwordsMatch
                          ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                          : 'text-red-500'
                      }`}
                    >
                      {passwordsMatch ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          <span>Passwords match</span>
                        </>
                      ) : (
                        <span>Passwords do not match yet</span>
                      )}
                    </p>
                  )}
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11 mt-2 font-medium gap-2 group"
            disabled={isLoading}
          >
            {isLoading ? (
              'Setting up account...'
            ) : (
              <>
                Accept & Join
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      </Form>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-border  text-center">
        <p className="text-[13px] text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => login()}
            className="font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};
